import { Typography, Box, Alert, Button } from "@kamstrup/kfl";
import { FC, useState } from "react";
import {
  PasswordStrength,
  PasswordStrengthMeter
} from "../passwordStrengthMeter/PasswordStrengthMeter";
import { CenteredCard } from "../../containers/card";
import { useTranslation } from "../../hooks/useTranslationHook/useTranslation";
import { commonStyles } from "../../styles/commonStyles";
import { useResetPassword } from "../../hooks/useResetPassword/useResetPassword";
import { toBase64 } from "../../utils/passwordEncoder/base64Encoder";
import { getLan } from "../../utils/languageManager";
import { NewPasswordDto } from "../../models/NewPasswordDto";

interface CompleteRegistrationStepProps {
  onSubmit: (dto: NewPasswordDto) => void;
  onCancel: () => Promise<void>;
  email: string;
  workflowId: string;
  languageOverride?: string | null;
}

export const ResetPasswordStep: FC<CompleteRegistrationStepProps> = ({
  onSubmit,
  email,
  workflowId,
  languageOverride
}) => {
  const { loading, error } = useResetPassword();
  const [newPassword, setNewPassword] = useState<string>("");
  const [passwordStrength, setPasswordStrength] = useState<string>("Too weak");
  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(false);
  const { translateString } = useTranslation();

  const isSubmitEnabled =
    passwordsMatch &&
    (passwordStrength === PasswordStrength.Good || passwordStrength === PasswordStrength.Strong);

  const handleSubmit = () => {
    if (isSubmitEnabled) {
      const encodedPassword = toBase64(newPassword);
      const languageToSend = languageOverride ?? getLan();

      const newPasswordPayload: NewPasswordDto = {
        email,
        password: encodedPassword,
        workflowId,
        language: languageToSend
      };
      onSubmit(newPasswordPayload);
    }
  };

  return (
    <CenteredCard title={translateString("change_password")}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Typography variant="body4" sx={commonStyles.introText}>
          {translateString("change_password_description")}
        </Typography>

        <Box sx={commonStyles.textFieldContainer}>
          <PasswordStrengthMeter
            onStrengthChange={(strength) => setPasswordStrength(strength)}
            onPasswordsMatch={(match) => setPasswordsMatch(match)}
            onPasswordChange={(value) => setNewPassword(value)}
            onEnter={handleSubmit}
            passwordField={translateString("new_password")}
            confirmPasswordField={translateString("confirm_new_password")}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ marginBottom: "16px" }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit" // 👈 IMPORTANT
          color="primary"
          variant="contained"
          fullWidth
          sx={commonStyles.button}
          disabled={!isSubmitEnabled || loading}
        >
          {loading ? translateString("submitting") : translateString("submit_new_password")}
        </Button>
      </form>
    </CenteredCard>
  );
};
